import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { getCategories } from '../../../../api/getCategories';
import searchIcon from '../../../../assets/icons/search.svg';

const getCategoryName = (category, language) => {
    switch (language) {
        case 'en': return category.eng_name;
        case 'kz': return category.kz_name;
        case 'cn': return category.ch_name;
        default:   return category.name;
    }
};

const getSubcategoryName = (sub, language) => {
    switch (language) {
        case 'en': return sub.eng_name;
        case 'kz': return sub.kz_name;
        case 'cn': return sub.ch_name;
        default:   return sub.name;
    }
};

const ChevronIcon = ({ open }) => (
    <svg
        className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
);

const CategoryModal = ({ onSelect, onClose, initialSelectedCategoryId, initialSelectedSubcategoryId }) => {
    const [categories, setCategories]         = useState([]);
    const [loading, setLoading]               = useState(true);
    const [searchTerm, setSearchTerm]         = useState('');
    const [expandedId, setExpandedId]         = useState(initialSelectedCategoryId || null);
    const [selectedCategoryId, setSelectedCategoryId]     = useState(initialSelectedCategoryId || null);
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(initialSelectedSubcategoryId || null);
    const searchRef = useRef(null);
    const { i18n } = useTranslation();

    useEffect(() => {
        getCategories()
            .then(setCategories)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        setTimeout(() => searchRef.current?.focus(), 350);
    }, []);

    const handleCategoryClick = (category) => {
        setExpandedId(prev => prev === category.id ? null : category.id);
    };

    const handleSubcategoryClick = (category, sub) => {
        setSelectedCategoryId(category.id);
        setSelectedSubcategoryId(sub.id);

        localStorage.setItem('selectedCategoryName',    getCategoryName(category, i18n.language));
        localStorage.setItem('selectedSubcategoryName', getSubcategoryName(sub, i18n.language));
        localStorage.setItem('selectedCategoryId',      category.id);
        localStorage.setItem('selectedSubcategoryId',   sub.id);

        if (onSelect) onSelect({ category, subcategory: sub });
        onClose();
    };

    const filtered = categories.filter(cat =>
        getCategoryName(cat, i18n.language).toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="flex flex-col h-full bg-white">

            {/* ── Header ── */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
                <div>
                    <h2 className="text-lg font-bold text-gray-900 m-0 leading-tight">{t('categories')}</h2>
                    <p className="text-xs text-gray-400 mt-0.5 m-0">
                        {categories.length > 0 ? `${categories.length} ${t('category') || 'категория'}` : ''}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-50 hover:text-red-500 transition-colors duration-200 cursor-pointer border-none text-gray-500"
                    aria-label="Жабу"
                >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            {/* ── Search ── */}
            <div className="px-4 py-3 flex-shrink-0">
                <div className="relative">
                    <img
                        src={searchIcon}
                        alt="search"
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40 pointer-events-none"
                    />
                    <input
                        ref={searchRef}
                        type="text"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={t('search') || 'Іздеу...'}
                        className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent transition-all"
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-gray-600 border-none bg-transparent cursor-pointer p-0"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                        </button>
                    )}
                </div>
            </div>

            {/* ── Category list ── */}
            <div className="flex-1 overflow-y-auto px-2 pb-6">

                {loading && (
                    <div className="flex flex-col gap-3 px-2 pt-2">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 animate-pulse">
                                <div className="w-14 h-14 bg-gray-200 rounded-xl flex-shrink-0" />
                                <div className="flex-1">
                                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && filtered.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <img src={searchIcon} alt="" className="w-6 h-6 opacity-40" />
                        </div>
                        <p className="text-gray-500 text-sm">Ештеңе табылмады</p>
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="mt-2 text-[var(--primary-color)] text-sm font-medium border-none bg-transparent cursor-pointer"
                        >
                            Тазалау
                        </button>
                    </div>
                )}

                {!loading && filtered.map((category) => {
                    const isExpanded = expandedId === category.id;
                    const isSelected = selectedCategoryId === category.id;
                    const name = getCategoryName(category, i18n.language);

                    return (
                        <div key={category.id} className="mb-1">
                            {/* Category row */}
                            <button
                                type="button"
                                onClick={() => handleCategoryClick(category)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 border-none text-left
                                    ${isSelected
                                        ? 'bg-[var(--primary-color)]/8 border-l-[3px] border-l-[var(--primary-color)]'
                                        : 'bg-transparent hover:bg-gray-50'
                                    }`}
                            >
                                {/* Category image */}
                                <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                                    {category.image_url ? (
                                        <img
                                            src={category.image_url}
                                            alt={name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                                            <span className="text-2xl">🌾</span>
                                        </div>
                                    )}
                                </div>

                                {/* Category name */}
                                <span className={`flex-1 text-sm font-semibold leading-snug ${isSelected ? 'text-[var(--primary-color)]' : 'text-gray-800'}`}>
                                    {name}
                                </span>

                                {/* Subcategory count badge */}
                                {category.subcategories?.length > 0 && (
                                    <span className="text-xs text-gray-400 font-medium mr-1 flex-shrink-0">
                                        {category.subcategories.length}
                                    </span>
                                )}

                                <ChevronIcon open={isExpanded} />
                            </button>

                            {/* Subcategories */}
                            {isExpanded && category.subcategories?.length > 0 && (
                                <div className="mx-3 mb-2 mt-1 pl-[68px]">
                                    <div className="flex flex-wrap gap-2">
                                        {category.subcategories.map(sub => {
                                            const isSubSelected = selectedSubcategoryId === sub.id && selectedCategoryId === category.id;
                                            return (
                                                <button
                                                    key={sub.id}
                                                    type="button"
                                                    onClick={() => handleSubcategoryClick(category, sub)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer border transition-all duration-150
                                                        ${isSubSelected
                                                            ? 'bg-[var(--primary-color)] text-white border-[var(--primary-color)] shadow-sm'
                                                            : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-[var(--primary-color)]/10 hover:text-[var(--primary-color)] hover:border-[var(--primary-color)]/30'
                                                        }`}
                                                >
                                                    {getSubcategoryName(sub, i18n.language)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CategoryModal;
